import React, { useState, useRef } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  AlertTriangle, 
  Bug, 
  Shield, 
  MessageSquare, 
  Upload, 
  X,
  Loader2,
  CheckCircle,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORT_TYPES = [
  { value: 'system_error', label: 'System Error', icon: AlertTriangle, color: 'text-red-500' },
  { value: 'spam_report', label: 'Report Spam/Abuse', icon: Shield, color: 'text-orange-500' },
  { value: 'bug', label: 'Bug Report', icon: Bug, color: 'text-yellow-500' },
  { value: 'feature_request', label: 'Feature Request', icon: MessageSquare, color: 'text-blue-500' },
  { value: 'other', label: 'Other', icon: MessageSquare, color: 'text-gray-500' }
];

export default function ReportModal({ open, onClose }) {
  const [reportType, setReportType] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [reportedEmail, setReportedEmail] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Screenshot must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setScreenshot(base64);
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!reportType || !subject || !description) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (reportType === 'spam_report' && !reportedEmail) {
      toast.error('Please enter the email address to report');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/support/reports`, {
        report_type: reportType,
        subject,
        description,
        reported_user_email: reportType === 'spam_report' ? reportedEmail : null,
        screenshot_data: screenshot
      });

      setSubmitted(true);
      toast.success('Report submitted successfully');
      
      // Reset form after delay
      setTimeout(() => {
        resetForm();
        onClose();
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setReportType('');
    setSubject('');
    setDescription('');
    setReportedEmail('');
    setScreenshot(null);
    setScreenshotPreview(null);
    setSubmitted(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const selectedType = REPORT_TYPES.find(t => t.value === reportType);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#6D4AFF]" />
            Report an Issue
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Help us improve by reporting errors, bugs, or abuse
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-12 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Report Submitted</h3>
            <p className="text-gray-400">
              Thank you for your feedback. Our team will review your report shortly.
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Report Type */}
            <div className="space-y-2">
              <Label className="text-gray-300">Report Type *</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="bg-[#252540] border-[#2d2d44] text-white">
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-[#2d2d44] z-[200]">
                  {REPORT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-white">
                      <div className="flex items-center gap-2">
                        <type.icon className={`w-4 h-4 ${type.color}`} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reported User Email (for spam reports) */}
            {reportType === 'spam_report' && (
              <div className="space-y-2">
                <Label className="text-gray-300">Email to Report *</Label>
                <Input
                  value={reportedEmail}
                  onChange={(e) => setReportedEmail(e.target.value)}
                  placeholder="spammer@mypatriotmail.org"
                  className="bg-[#252540] border-[#2d2d44] text-white"
                />
                <p className="text-xs text-gray-500">
                  Enter the email address of the user you want to report
                </p>
              </div>
            )}

            {/* Subject */}
            <div className="space-y-2">
              <Label className="text-gray-300">Subject *</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief description of the issue"
                className="bg-[#252540] border-[#2d2d44] text-white"
                maxLength={100}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-gray-300">Description *</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please provide as much detail as possible..."
                className="bg-[#252540] border-[#2d2d44] text-white min-h-[120px]"
                maxLength={2000}
              />
              <p className="text-xs text-gray-500 text-right">
                {description.length}/2000
              </p>
            </div>

            {/* Screenshot Upload */}
            <div className="space-y-2">
              <Label className="text-gray-300">Screenshot (Optional)</Label>
              
              {screenshotPreview ? (
                <div className="relative">
                  <img 
                    src={screenshotPreview} 
                    alt="Screenshot preview" 
                    className="w-full max-h-48 object-contain rounded-lg border border-[#2d2d44]"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={removeScreenshot}
                    className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-500 text-white rounded-full w-8 h-8"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-[#2d2d44] rounded-lg p-6 text-center cursor-pointer hover:border-[#6D4AFF] transition-colors"
                >
                  <ImageIcon className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">
                    Click to upload a screenshot
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG up to 5MB
                  </p>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {/* Type-specific tips */}
            {selectedType && (
              <div className={`p-3 rounded-lg bg-${selectedType.color.replace('text-', '')}/10 border border-${selectedType.color.replace('text-', '')}/30`}
                   style={{ 
                     backgroundColor: reportType === 'system_error' ? 'rgba(239, 68, 68, 0.1)' :
                                      reportType === 'spam_report' ? 'rgba(249, 115, 22, 0.1)' :
                                      reportType === 'bug' ? 'rgba(234, 179, 8, 0.1)' :
                                      reportType === 'feature_request' ? 'rgba(59, 130, 246, 0.1)' :
                                      'rgba(107, 114, 128, 0.1)',
                     borderColor: reportType === 'system_error' ? 'rgba(239, 68, 68, 0.3)' :
                                  reportType === 'spam_report' ? 'rgba(249, 115, 22, 0.3)' :
                                  reportType === 'bug' ? 'rgba(234, 179, 8, 0.3)' :
                                  reportType === 'feature_request' ? 'rgba(59, 130, 246, 0.3)' :
                                  'rgba(107, 114, 128, 0.3)'
                   }}>
                <p className="text-xs text-gray-300">
                  {reportType === 'system_error' && '💡 Include any error messages you see and the steps that led to the error.'}
                  {reportType === 'spam_report' && '💡 Our team will investigate the reported user and take appropriate action.'}
                  {reportType === 'bug' && '💡 Include steps to reproduce the bug and what you expected to happen.'}
                  {reportType === 'feature_request' && '💡 Describe your idea and how it would improve your experience.'}
                  {reportType === 'other' && '💡 Please provide as much context as possible.'}
                </p>
              </div>
            )}
          </div>
        )}

        {!submitted && (
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="ghost" 
              onClick={handleClose}
              className="text-gray-400"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={submitting || !reportType || !subject || !description}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Report'
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
